import "./App.css";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import PremiumSelector from "./components/PremiumSelector";
import CurrencyInput from "./components/CurrencyInput";
import AnimatedNumber from "./components/AnimatedNumber";
import HelpButton from "./components/HelpButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalculator,
  faAngleDoubleDown,
  faYenSign,
  faAngleRight,
} from "@fortawesome/free-solid-svg-icons";
import { Bs1SquareFill, Bs2SquareFill, Bs3SquareFill } from "react-icons/bs";

function App() {
  const { t } = useTranslation();
  const [resetFlag, setResetFlag] = useState(false);

  const defaultSalary = 58000;

  const [salary, setSalary] = useState(defaultSalary);
  const [paid, setPaid] = useState(false);
  const [calcResult, setCalcResult] = useState({
    sum: 0,
    reimburse: 0,
    ceilingAmount: 0,
    additionalBenefit: 0,
    total: 0,
    remaining: 0,
    paid: 0,
  });

  useEffect(() => {
    const sum = parseInt(paid / 3, 10) * 10;
    let reimburse = 0;
    let ceilingAmount = 0;
    let additionalBenefit = 0;
    if (salary === 0 || paid === 0) return;
    // 高額療養費 自己負担限度額（令和8年8月診療分〜／改正後）
    // ※数値は健康保険法改正に基づく全国共通値。年間上限は試算に含めず注釈のみ。
    if (salary <= 260000) {
      ceilingAmount = 61500;
    } else if (salary <= 500000) {
      ceilingAmount = 85800 + (sum - 286000) * 0.01;
    } else if (salary <= 790000) {
      ceilingAmount = 179100 + (sum - 597000) * 0.01;
    } else {
      ceilingAmount = 270300 + (sum - 901000) * 0.01;
    }
    ceilingAmount = Math.floor(ceilingAmount); // 円未満切り捨て
    if (paid > ceilingAmount) {
      reimburse = paid - ceilingAmount;
    } else {
      reimburse = 0;
    }
    if (paid - reimburse > 20999) {
      additionalBenefit = paid - reimburse - 20000;
      additionalBenefit = parseInt(additionalBenefit / 100, 10) * 100; // 100円未満切り捨て
    }
    if (additionalBenefit < 1000) {
      additionalBenefit = 0;
    }
    setCalcResult({
      sum: sum,
      reimburse: reimburse,
      ceilingAmount: ceilingAmount,
      additionalBenefit: additionalBenefit,
      total: reimburse + additionalBenefit,
      remaining: paid - (reimburse + additionalBenefit),
      paid: paid,
    });
  }, [salary, paid]);

  const handlePremiumChange = (value) => {
    setSalary(value);
  };

  const handleInputChange = (value) => {
    setPaid(parseInt(value, 10));
    if (value === "") {
      reset();
    }
  };

  const reset = () => {
    setPaid(0);
    setCalcResult({
      sum: 0,
      reimburse: 0,
      ceilingAmount: 0,
      additionalBenefit: 0,
      total: 0,
      remaining: 0,
    });
    setResetFlag((prevFlag) => !prevFlag);
  };

  return (
    <div className="App">
      <div className="input-panel">
        <div
          style={{
            backgroundColor: "#fff8e1",
            color: "#5d4037",
            border: "1px solid #ffe082",
            borderRadius: "6px",
            padding: "0.7rem 1rem",
            marginBottom: "1.2rem",
            fontSize: "0.95rem",
            fontWeight: 600,
            lineHeight: 1.5,
          }}
        >
          {t(
            "このツールは令和8年8月の法改正に基づいて計算します。なお、皆さんの自己負担額は当組合独自の付加給付があるため変更ありません。"
          )}
        </div>
        <div className="form-group">
          <label htmlFor="currency-input">
            <span style={{color: '#bbdefb', paddingRight: '0.3rem', verticalAlign: 'top', fontSize: '.9rem'}}><FontAwesomeIcon icon={faAngleRight} /></span>
            {t("Amount paid at the counter")}
            <HelpButton>
              <p>
                {t(
                  "病院の窓口で支払った１ヵ月の医療費を入力してください。自費診療、入院時の食事代や居住費・差額ベッド代等は対象外です。"
                )}
              </p>
            </HelpButton>
          </label>
          <CurrencyInput
            id="currency-input"
            onChange={handleInputChange}
            resetFlag={resetFlag}
          />
          <div
            style={{
              display: calcResult.sum !== 0 ? "block" : "none",
              fontSize: "1rem",
              width: "100%",
              marginTop: "0.5rem",
              marginLeft: "0.5rem",
            }}
          >
            {t("医療費総額")}:
            <strong>
              {parseInt(calcResult.sum, 10).toLocaleString()}
              {t("円")}
            </strong>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="premium-selector">
            <span style={{color: '#bbdefb', paddingRight: '0.3rem', verticalAlign: 'top', fontSize: '.9rem'}}><FontAwesomeIcon icon={faAngleRight} /></span>
            {t("Your Health Insurance Premium")}
            <HelpButton>
              <p>
                {t(
                  "高額療養費の自己負担限度額の計算に必要です。給与明細などでご確認いただけます。"
                )}
              </p>
            </HelpButton> 
            <span style={{fontSize: '.85rem', paddingLeft: '.5rem'}}>{t('※給与明細でご確認ください')}</span>
          </label>
          <PremiumSelector
            id="premium-selector"
            onChange={handlePremiumChange}
            defaultValue={defaultSalary}
          />
        </div>
        <div
          style={{
            fontSize: "0.8rem",
            lineHeight: 1.5,
            marginTop: "0.5rem",
            opacity: 0.9,
          }}
        >
          <p style={{ margin: "0 0 0.3rem" }}>
            {t(
              "標準報酬月額28万〜50万円の区分には外来等の年間上限（53万円/年）がありますが、本ツールの試算には反映していません。"
            )}
          </p>
          <p style={{ margin: 0 }}>
            {t(
              "当組合の付加給付（自己負担上限2万円）により、限度額改定後も最終的な自己負担額はほぼ変わりません（変わるのは高額療養費と付加給付の内訳です）。"
            )}
          </p>
        </div>
      </div>
      <div
        className="calc-result"
        style={{
          display:
            calcResult.total > 0 && calcResult.paid !== calcResult.remaining
              ? "block"
              : "none",
        }}
      >
        <div
          style={{
            fontSize: "1.35rem",
            marginBottom: ".5rem",
            fontWeight: "700",
            color: "#2566ac",
          }}
        >
          <span style={{ marginRight: "0.5rem" }}>
            <FontAwesomeIcon icon={faCalculator} />
          </span>
          {t("払い戻される額")}
        </div>
        <div id="result-reimburse">
          <label htmlFor="result-reimburse">
            <span>
              <Bs1SquareFill />
            </span>
            {t("高額療養費")}
          </label>
          <AnimatedNumber number={calcResult.reimburse} />
        </div>
        <div
          style={{
            display: calcResult.reimburse === 0 ? "inline-block" : "none",
          }}
        >
          {t(
            "自己負担限度額 {{reimburse}} 円を超えていないため高額療養費は支給されません",
            { reimburse: calcResult.ceilingAmount.toLocaleString() }
          )}
          <HelpButton>
            <p>
              {t(
                "高額療養費制度の自己負担限度額は被保険者の標準報酬月額と医療費から計算できます。"
              )}
              {t("詳しくは")}
              <a href="#content">{t("こちら")}</a>
              {t("をご覧ください。")}
            </p>
          </HelpButton>
        </div>
        <table
          className="formulaTable"
          style={{ display: calcResult.reimburse > 0 ? "table" : "none" }}
        >
          <tbody>
            <tr>
              <td>{t("窓口で支払った金額")}</td>
              <td></td>
              <td>{parseInt(calcResult.paid, 10).toLocaleString()}</td>
            </tr>
            <tr>
              <td>
                {t("自己負担限度額")}
                <HelpButton>
                  <p>
                    {t(
                      "高額療養費制度の自己負担限度額は被保険者の標準報酬月額と医療費から計算できます。"
                    )}
                    {t("詳しくは")}
                    <a href="#content">{t("こちら")}</a>
                    {t("をご覧ください。")}
                  </p>
                </HelpButton>
              </td>
              <td>&minus;</td>
              <td>{calcResult.ceilingAmount.toLocaleString()}</td>
            </tr>
            <tr>
              <td></td>
              <td className="sumRow"></td>
              <td className="sumRow">
                {calcResult.reimburse.toLocaleString()}
              </td>
            </tr>
          </tbody>
        </table>
        <div id="result-benefit">
          <label htmlFor="result-benefit">
            <span>
              <Bs2SquareFill />
            </span>
            {t("additionalBenefit")}
          </label>
          <AnimatedNumber number={calcResult.additionalBenefit} />
        </div>
        <table className="formulaTable">
          <tbody>
            <tr
              style={{
                display: calcResult.reimburse === 0 ? "table-row" : "none",
              }}
            >
              <td>
                <span style={{ color: "#418CDD" }}>
                  <Bs1SquareFill />
                </span>{" "}
                {t("窓口で支払った金額")}
              </td>
              <td></td>
              <td>{parseInt(calcResult.paid, 10).toLocaleString()}</td>
            </tr>
            <tr
              style={{
                display: calcResult.reimburse !== 0 ? "table-row" : "none",
              }}
            >
              <td>{t("自己負担限度額")}</td>
              <td></td>
              <td>{parseInt(calcResult.ceilingAmount, 10).toLocaleString()}</td>
            </tr>
            <tr>
              <td>
                {t("自己負担上限")}
                <HelpButton>
                  <p>
                    {t(
                      "マイクロソフト健康保険組合独自の付加給付として自己負担額が２万円を超えた分が一部負担還元として支給されます。"
                    )}
                    {t("詳しくは")}
                    <a href="https://www.microsoftkenpo.or.jp/member/benefit/sick.html">
                      {t("こちら")}
                    </a>
                    {t("をご覧ください。")}
                  </p>
                </HelpButton>
              </td>
              <td>&minus;</td>
              <td>20,000</td>
            </tr>
            <tr>
              <td></td>
              <td className="sumRow"></td>
              <td className="sumRow">
                {calcResult.additionalBenefit.toLocaleString()}
              </td>
            </tr>
          </tbody>
          <caption>
            {t("※1,000円未満不支給。100円未満の端数切り捨て。")}
          </caption>
        </table>
        <div
          id="result-total"
          style={{
            display: calcResult.total !== 0 ? "flex" : "none",
          }}
        >
          <label htmlFor="result-total">
            <span>
              <Bs3SquareFill />
            </span>
            {t("合計")}&nbsp; (
            <span className="n1">
              <Bs1SquareFill />
            </span>
            {t("+")}
            <span className="n2">
              <Bs2SquareFill />
            </span>
            )
          </label>
          <AnimatedNumber number={calcResult.total} />
        </div>
      </div>
      <div
        class="downArrow"
        style={{ display: calcResult.sum === 0 ? "none" : "block" }}
      >
        <FontAwesomeIcon icon={faAngleDoubleDown} />
      </div>
      <div
        className="calc-result"
        style={{
          display:
            calcResult.total > 0 && calcResult.paid !== calcResult.remaining
              ? "block"
              : "none",
          borderWidth: "2px",
          borderColor: "#2566ac",
        }}
      >
        <div
          style={{
            fontSize: "1.35rem",
            marginBottom: ".5rem",
            fontWeight: "700",
            color: "#2566ac",
          }}
        >
          <span style={{ marginRight: "0.5rem" }}>
            <FontAwesomeIcon icon={faYenSign} />
          </span>
          {t("remaining")}
        </div>
        <div
          id="result-remaining"
          style={{
            display: calcResult.remaining !== calcResult.paid ? "flex" : "none",
          }}
        >
          <label htmlFor="result-remaining">
            <span className="n1">{t("窓口で支払った金額")}<br />{paid.toLocaleString()}{t('円')}</span>
            {t("−")}&nbsp;
            <span className="n1">{t("払い戻される額")}<br />{calcResult.total.toLocaleString()}{t('円')}</span>
          </label>
          <AnimatedNumber number={calcResult.remaining} />
        </div>
      </div>
      <div
        className="calc-result"
        style={{
          display: calcResult.remaining === calcResult.paid ? "block" : "none",
        }}
      >
        {t("払い戻しはありません。")}
      </div>
      <div
        className="calc-result"
        style={{ display: calcResult.sum === 0 ? "block" : "none" }}
      >
        <ul>
          <li>
            {t(
              "自費診療ならびに入院時の食事代や居住費・差額ベッド代は、対象となる費用には含まれません。"
            )}
          </li>
          <li>
            {t(
              "計算結果はあくまで概算です。状況により実際の支給金額と誤差が生じる場合がございますのでご了承ください。"
            )}
          </li>
        </ul>
      </div>
    </div>
  );
}

export default App;
